import { Alert, AlertTitle, Button } from "@mui/material";
import { FlowButtons } from "components/NewOrders/NewOrders.styles";
import { NewOrderBack } from "components/NewOrders/constants";
import TrucksHeader from "components/Trucks/TrucksHeader/TrucksHeader";
import {
	CantProcessRequestText,
	NewOrderPleaseContactCare,
	ReplacementReviewStepperText,
	ServerTemporarilyUnavailableText,
	SubmitTruckOrderButtonText,
	TruckCubeCountErrorText,
	TruckErrorBannerAddLineItemsBullet,
	TruckErrorBannerChangePOBullet,
	TruckErrorBannerProvidePOBullet,
	TruckErrorBannerRefreshBullet,
	TruckErrorBannerSubheader
} from "constants/text";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { RootState } from "stores/application.store";
import { ReviewTrucksContentWrapper, TruckSubheaderWrapper } from "./ReviewTrucksPage.styles";
import ReviewTruckStopOrderCard from "components/Trucks/components/ReviewTruckStopOrderCard/ReviewTruckStopOrderCard";
import ReviewTruckDetails from "components/Trucks/components/ReviewTruckDetails/ReviewTruckDetails";
import { useSaveTruckMutation, useSubmitTruckMutation } from "features/api/truckApi";
import { models } from "types/api/viewModels";
import ReviewTruckDetailsSkeleton from "components/Trucks/skeletons/ReviewTrucksPageSkeletons/ReviewTruckDetailsSkeleton.tsx";
import { useEffect, useState } from "react";
import { truckActions } from "features/reducers/truck/truck.ts";
import { isApiErrorResponse } from "features/api/utils/apiUtils.ts";
import StopDetails from "components/Trucks/components/StopDetails/StopDetails";
import { Page } from "components/Common/Page";

const ReviewTrucksPage = () => {
	const [submitTruck, { isLoading: isSubmitting, error }] = useSubmitTruckMutation();
	const storedTruck = useSelector((state: RootState) => state?.truck?.truck);
	const truckStops = storedTruck?.stops;
	const [erroredTruckOrders, setErroredTruckOrders] = useState<any[]>([]);
	const overCubeCapacityError = Boolean(
		isApiErrorResponse(error) &&
			error?.errors?.some((error) => error.message?.includes("Truck is over max cube capacity"))
	);
	const [saveTruck] = useSaveTruckMutation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const handleBack = () => {
		navigate("/trucks/add-order");
	};

	const handleSubmit = async (truck: models["TruckViewModel"]) => {
		if (!truck?.truckId || !truck) return;

		try {
			await saveTruck(truck)
				.unwrap()
				.then(async (response) => {
					if (response) {
						dispatch(truckActions.updateTruck(response));
					} else {
						throw Error();
					}
				});
			await submitTruck(truck.truckId).unwrap();
			navigate("/trucks/confirmation");
		} catch (error: any) {
			const errorResponse =
				"errors" in error &&
				error.errors.flatMap((err: any) => (err.errorDetails ? Object.entries(err.errorDetails) : []));
			setErroredTruckOrders(errorResponse);
			window.scrollTo(0, 0);
		}
	};

	// Make the save truck call when the user refreshes the page
	useEffect(() => {
		if (storedTruck?.truckId) {
			saveTruck(storedTruck)
				.unwrap()
				.then((response) => {
					if (response) {
						dispatch(truckActions.updateTruck(response));
					}
				})
				.catch((error) => {
					console.error("Failed to save truck on refresh:", error);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Page
			variant="orders"
			data-testid="trucks-review-trucks-page"
		>
			<TrucksHeader />
			<ReviewTrucksContentWrapper data-testid="review-trucks-content-wrapper">
				<TruckSubheaderWrapper data-testid="truck-subheader">
					<h5>{ReplacementReviewStepperText}</h5>
				</TruckSubheaderWrapper>
				{error && (
					<Alert severity="error">
						<AlertTitle>{CantProcessRequestText}</AlertTitle>
						{error && "status" in error && error.status == 400 && <div>{TruckErrorBannerSubheader}</div>}
						{error && "status" in error && error.status != 400 && (
							<div>{ServerTemporarilyUnavailableText}</div>
						)}
						<ul>
							{erroredTruckOrders?.find((order) => order[1][0].code === 40007) && (
								<li>{TruckErrorBannerProvidePOBullet}</li>
							)}
							{erroredTruckOrders?.find(
								(order) => order[1][0].code === 40009 || order[1][0].code === 400
							) && <li>{TruckErrorBannerChangePOBullet}</li>}
							{erroredTruckOrders?.find((order) => order[1][0].code === 40008) && (
								<li>{TruckErrorBannerAddLineItemsBullet}</li>
							)}
							{overCubeCapacityError && <li>{TruckCubeCountErrorText}</li>}
							{(overCubeCapacityError ||
								erroredTruckOrders?.find(
									(order) =>
										order[1][0].code === 40007 ||
										order[1][0].code === 40009 ||
										order[1][0].code === 400
								)) && <li>{TruckErrorBannerRefreshBullet}</li>}
						</ul>
						{error && "status" in error && error.status == 400 && <div>{NewOrderPleaseContactCare}</div>}
						{error && "status" in error && (error.status == 500 || error.status == 404) && (
							<p>({error.status} Error)</p>
						)}
					</Alert>
				)}
				{isSubmitting && <ReviewTruckDetailsSkeleton />}
				{storedTruck && !isSubmitting && <ReviewTruckDetails data-testid="review-truck-details" />}
				{truckStops?.map((stop, index) => (
					<div
						key={stop.truckStopId}
						data-testid={`truck-stop-${index}`}
					>
						<StopDetails
							stop={stop}
							index={index}
						/>
						{stop.draftOrders?.map((order, orderIndex) => {
							const findErroredOrder =
								erroredTruckOrders &&
								erroredTruckOrders?.find((erroredOrder) => erroredOrder[0] === order.draftOrderId);
							return order.configurations?.map((configuration, configIndex) => (
								<ReviewTruckStopOrderCard
									data-testid={`review-truck-stop-order-card-${index}-${orderIndex}-${configIndex}`}
									key={`${orderIndex}-${configIndex}`}
									configuration={configuration}
									address={stop.address}
									shipToAddress={order.shipToAddress}
									poNumber={order.poNumber}
									designerNumber={order.designerNumber}
									jobName={order.jobName}
									labelComments={order.labelComments}
									truckOrderError={findErroredOrder?.[1]?.[0].code}
								/>
							));
						})}
					</div>
				))}
				<FlowButtons data-testid="flow-buttons">
					<Button
						data-testid="trucks-add-orders-back-button"
						variant="text"
						onClick={handleBack}
					>
						{NewOrderBack}
					</Button>
					<Button
						data-testid="trucks-add-orders-continue-button"
						onClick={() => storedTruck && handleSubmit(storedTruck)}
						variant="contained"
						disabled={isSubmitting}
					>
						{SubmitTruckOrderButtonText}
					</Button>
				</FlowButtons>
			</ReviewTrucksContentWrapper>
		</Page>
	);
};

export default ReviewTrucksPage;
